// src/services/negotiationService.ts - SCRIPT COMPLET
import api from './api';

export interface PriceNegotiation {
  id: number;
  product_id: number;
  customer_id: number;
  farmer_id: number;
  original_price: number;
  proposed_price: number;
  counter_offer_price?: number;
  quantity: number;
  status: string;
  customer_proposals_count: number;
  expires_at: string;
  created_at: string;
  updated_at: string;
}

export interface PriceNegotiationCreate {
  product_id: number;
  proposed_price: number;
  quantity: number;
}

export interface PriceNegotiationResponse {
  action: 'accept' | 'reject' | 'counter_offer';
  counter_offer_price?: number;
}

export interface ActiveNegotiationInfo {
  has_active_negotiation: boolean;
  negotiation?: PriceNegotiation;
  can_propose_again?: boolean;
  proposals_left?: number;
}

export interface NegotiatedPriceInfo {
  has_negotiated_price: boolean;
  negotiated_price?: number;
  original_price?: number;
  quantity?: number;
  negotiation_id?: number;
  savings?: number;
}

export interface NegotiationStats {
  total_negotiations: number;
  accepted_negotiations: number;
  pending_negotiations: number;
  success_rate?: number;
}

// Statuts et couleurs
export const NEGOTIATION_STATUSES = {
  pending: 'En attente',
  accepted: 'Acceptée',
  rejected: 'Refusée',
  counter_offered: 'Contre-offre',
  expired: 'Expirée'
} as const;

export const NEGOTIATION_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  counter_offered: 'bg-blue-100 text-blue-800',
  expired: 'bg-gray-100 text-gray-800'
} as const;

class NegotiationService {
  
  // ===== POUR LES CUSTOMERS =====
  
  async proposePrice(negotiationData: PriceNegotiationCreate): Promise<PriceNegotiation> {
    try {
      console.log('💰 Proposition de prix:', negotiationData);
      
      const response = await api.post('/negotiations/propose', negotiationData);
      
      console.log('✅ Proposition envoyée:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur lors de la proposition:', error);
      throw error;
    }
  }

  async getActiveNegotiationForProduct(productId: number): Promise<ActiveNegotiationInfo> {
    try {
      console.log('🔍 Vérification négociation active:', productId);
      
      const response = await api.get(`/negotiations/product/${productId}/active`);
      
      console.log('✅ Info négociation active:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur vérification négociation active:', error);
      throw error;
    }
  }

  async acceptCounterOffer(negotiationId: number): Promise<any> {
    try {
      console.log('✅ Acceptation contre-offre:', negotiationId);
      
      const response = await api.post(`/negotiations/${negotiationId}/accept-counter-offer`);
      
      console.log('✅ Contre-offre acceptée:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur acceptation contre-offre:', error);
      throw error;
    }
  }

  async getCustomerNegotiations(status?: string): Promise<PriceNegotiation[]> {
    try {
      console.log('🔍 Récupération négociations customer:', { status });
      
      const params = status ? { status } : {};
      const response = await api.get('/negotiations/my-negotiations', { params });
      
      console.log('✅ Négociations récupérées:', response.data.length);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération négociations customer:', error);
      throw error;
    }
  }

  // ===== POUR LES FARMERS =====
  
  async getFarmerNegotiations(status?: string): Promise<PriceNegotiation[]> {
    try {
      console.log('🔍 Récupération négociations farmer:', { status });
      
      const params = status ? { status } : {};
      const response = await api.get('/negotiations/farmer/received', { params });
      
      console.log('✅ Négociations farmer récupérées:', response.data.length);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération négociations farmer:', error);
      throw error;
    }
  }

  async respondToNegotiation(
    negotiationId: number, 
    response: PriceNegotiationResponse
  ): Promise<any> {
    try {
      console.log('📝 Réponse à négociation:', { negotiationId, response });
      
      const result = await api.put(`/negotiations/${negotiationId}/respond`, response);
      
      console.log('✅ Réponse envoyée:', result.data);
      return result.data;
      
    } catch (error) {
      console.error('❌ Erreur réponse négociation:', error);
      throw error;
    }
  }

  async getFarmerStats(): Promise<NegotiationStats> {
    try {
      console.log('📊 Récupération stats farmer');
      
      const response = await api.get('/negotiations/stats/farmer');
      
      console.log('✅ Stats farmer récupérées:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur stats farmer:', error);
      throw error;
    }
  }

  async getCustomerStats(): Promise<NegotiationStats> {
    try {
      console.log('📊 Récupération stats customer');
      
      const response = await api.get('/negotiations/stats/customer');
      
      console.log('✅ Stats customer récupérées:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur stats customer:', error);
      throw error;
    }
  }

  // ===== POUR LE CHECKOUT =====
  
  async getNegotiatedPrice(productId: number): Promise<NegotiatedPriceInfo> {
    try {
      console.log('🔍 Récupération prix négocié:', productId);
      
      const response = await api.get(`/negotiations/product/${productId}/negotiated-price`);
      
      console.log('✅ Prix négocié récupéré:', response.data);
      return response.data;
      
    } catch (error) {
      console.error('❌ Erreur récupération prix négocié:', error);
      return { has_negotiated_price: false };
    }
  }

  // ===== UTILITAIRES =====

  getStatusLabel(status: string): string {
    return NEGOTIATION_STATUSES[status as keyof typeof NEGOTIATION_STATUSES] || status;
  }

  getStatusColor(status: string): string {
    return NEGOTIATION_STATUS_COLORS[status as keyof typeof NEGOTIATION_STATUS_COLORS] || 'bg-gray-100 text-gray-800';
  }

  formatPrice(price: number): string {
    return `${price.toLocaleString()} FCFA`;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  isActive(negotiation: PriceNegotiation): boolean {
    return ['pending', 'counter_offered'].includes(negotiation.status);
  }

  isExpired(negotiation: PriceNegotiation): boolean {
    return new Date(negotiation.expires_at) < new Date();
  }

  getFinalPrice(negotiation: PriceNegotiation): number {
    if (negotiation.status === 'accepted') {
      return negotiation.counter_offer_price || negotiation.proposed_price;
    }
    return negotiation.original_price;
  }

  calculateSavings(negotiation: PriceNegotiation): number {
    const finalPrice = this.getFinalPrice(negotiation);
    return negotiation.original_price - finalPrice;
  }

  calculateDiscountPercentage(negotiation: PriceNegotiation): number {
    const savings = this.calculateSavings(negotiation);
    return (savings / negotiation.original_price) * 100;
  }

  getTimeUntilExpiration(negotiation: PriceNegotiation): string {
    const now = new Date();
    const expiry = new Date(negotiation.expires_at);
    const diff = expiry.getTime() - now.getTime();
    
    if (diff <= 0) {
      return 'Expirée';
    }
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m restantes`;
    } else {
      return `${minutes}m restantes`;
    }
  }

  validateProposal(
    proposedPrice: number, 
    originalPrice: number, 
    quantity: number
  ): string[] {
    const errors: string[] = [];
    
    if (!proposedPrice || proposedPrice <= 0) {
      errors.push('Le prix proposé doit être supérieur à 0');
    }
    
    if (proposedPrice >= originalPrice) {
      errors.push('Le prix proposé doit être inférieur au prix affiché');
    }
    
    if (proposedPrice < originalPrice * 0.1) {
      errors.push('Le prix proposé est trop bas (minimum 10% du prix original)');
    }
    
    if (!quantity || quantity <= 0) {
      errors.push('La quantité doit être supérieure à 0');
    }
    
    return errors;
  }

  validateCounterOffer(
    counterOfferPrice: number,
    proposedPrice: number,
    originalPrice: number
  ): string[] {
    const errors: string[] = [];
    
    if (!counterOfferPrice || counterOfferPrice <= 0) {
      errors.push('Le prix de contre-offre doit être supérieur à 0');
    }
    
    if (counterOfferPrice > originalPrice) {
      errors.push('La contre-offre ne peut pas dépasser le prix original');
    }
    
    if (counterOfferPrice < proposedPrice) {
      errors.push('La contre-offre doit être supérieure à la proposition du client');
    }
    
    return errors;
  }

  getStatusBadgeClass(status: string): string {
    const baseClasses = 'px-2 py-1 rounded-full text-xs font-medium';
    const colorClass = this.getStatusColor(status);
    return `${baseClasses} ${colorClass}`;
  }

  getActionMessage(action: string, negotiation: PriceNegotiation): string {
    switch (action) {
      case 'accept':
        return `Proposition acceptée ! Prix final : ${this.formatPrice(negotiation.proposed_price)}`;
      case 'reject':
        return 'Proposition refusée';
      case 'counter_offer':
        return `Contre-offre envoyée : ${this.formatPrice(negotiation.counter_offer_price || 0)}`;
      case 'accept_counter_offer':
        return `Contre-offre acceptée ! Prix final : ${this.formatPrice(negotiation.counter_offer_price || 0)}`;
      default:
        return 'Action effectuée';
    }
  }
}

export default new NegotiationService();